import { useEffect, useState } from "react";
import { Phone, Menu, X } from "lucide-react";
import { CONTACT } from "@/lib/site";

const LINKS = [
  { href: "#rooms", label: "Rooms" },
  { href: "#experience", label: "Experience" },
  { href: "#amenities", label: "Amenities" },
  { href: "#gallery", label: "Gallery" },
  { href: "#location", label: "Location" },
  { href: "#book", label: "Book" },
];

export default function Nav() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-nav"
      className={`fixed top-4 sm:top-6 left-1/2 -translate-x-1/2 z-50 w-[94%] max-w-6xl transition-all duration-500 ${
        scrolled ? "sm:top-3" : ""
      }`}
    >
      <div className="sh-nav-pill rounded-full pl-5 pr-2 py-2 flex items-center justify-between gap-4">
        {/* Logo */}
        <a
          href="#top"
          data-testid="nav-logo"
          className="flex items-center gap-2 shrink-0"
        >
          <span className="w-2 h-2 rounded-full bg-[color:var(--sh-amber)]" />
          <span className="sh-serif text-[color:var(--sh-cream)] text-base sm:text-lg tracking-tight">
            Sardar House
          </span>
        </a>

        {/* Desktop links */}
        <nav className="hidden lg:flex items-center gap-1">
          {LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className="sh-link text-sm px-3 py-2"
            >
              {l.label}
            </a>
          ))}
        </nav>

        {/* Right CTA */}
        <div className="flex items-center gap-2">
          <a
            href={`tel:${CONTACT.phoneRaw}`}
            data-testid="nav-phone"
            className="hidden sm:inline-flex items-center gap-2 px-4 py-2 text-sm text-[color:var(--sh-cream-dim)] hover:text-[color:var(--sh-amber)] transition"
          >
            <Phone size={14} strokeWidth={1.6} />
            <span className="tabular-nums">{CONTACT.phone}</span>
          </a>
          <a
            href="#book"
            data-testid="nav-book-btn"
            className="sh-amber-btn hidden sm:inline-flex items-center px-5 py-2.5 text-sm"
          >
            Book Your Stay
          </a>
          <button
            data-testid="nav-mobile-toggle"
            aria-label="Toggle menu"
            className="lg:hidden inline-flex items-center justify-center w-10 h-10 rounded-full border border-[color:var(--sh-line)] text-[color:var(--sh-cream)]"
            onClick={() => setOpen((v) => !v)}
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      {open && (
        <div
          data-testid="nav-mobile-menu"
          className="lg:hidden mt-3 mx-auto sh-nav-pill rounded-3xl p-3 flex flex-col"
        >
          {LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              onClick={() => setOpen(false)}
              className="px-4 py-3 text-sm text-[color:var(--sh-cream-dim)] hover:text-[color:var(--sh-amber)]"
              data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
            >
              {l.label}
            </a>
          ))}
          <a
            href={`tel:${CONTACT.phoneRaw}`}
            className="px-4 py-3 text-sm text-[color:var(--sh-amber)] flex items-center gap-2"
            data-testid="nav-mobile-phone"
          >
            <Phone size={14} /> {CONTACT.phone}
          </a>
          <a
            href="#book"
            onClick={() => setOpen(false)}
            className="sh-amber-btn text-center mt-2 py-3 text-sm"
            data-testid="nav-mobile-book-btn"
          >
            Book Your Stay
          </a>
        </div>
      )}
    </header>
  );
}
